import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { getApiBase } from './Config';
import { handleAuthFailure } from './auth';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function DiscountCodesTab() {
  const [activeCodes, setActiveCodes] = useState([]);
  const [inactiveCodes, setInactiveCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    code: '',
    discount_percent: '',
    valid_from: '',
    valid_to: '',
    max_redemptions: '',
  });

  const authHeaders = () => ({
    Authorization: `Bearer ${Cookies.get('access_token')}`,
    'Content-Type': 'application/json',
  });

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = getApiBase();
      const [activeRes, inactiveRes] = await Promise.all([
        fetch(`${base}/admin/promo-codes?status=active&product=eeg_device`, { headers: authHeaders() }),
        fetch(`${base}/admin/promo-codes?status=inactive&product=eeg_device`, { headers: authHeaders() }),
      ]);
      if (handleAuthFailure(activeRes) || handleAuthFailure(inactiveRes)) return;
      if (!activeRes.ok || !inactiveRes.ok) throw new Error('Failed to load discount codes');
      setActiveCodes(await activeRes.json());
      setInactiveCodes(await inactiveRes.json());
    } catch (e) {
      setError(e.message || 'Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        product: 'eeg_device',
        discount_percent: parseInt(form.discount_percent, 10),
        valid_from: form.valid_from,
        valid_to: form.valid_to,
        max_redemptions: form.max_redemptions.trim()
          ? parseInt(form.max_redemptions, 10)
          : null,
      };
      const res = await fetch(`${getApiBase()}/admin/promo-codes`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (handleAuthFailure(res)) return;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create code');
      }
      setShowModal(false);
      setForm({ code: '', discount_percent: '', valid_from: '', valid_to: '', max_redemptions: '' });
      await fetchCodes();
    } catch (e) {
      setError(e.message || 'Failed to create code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (id) => {
    if (!window.confirm('Disable this discount code?')) return;
    try {
      const res = await fetch(`${getApiBase()}/admin/promo-codes/${id}/disable`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to disable code');
      await fetchCodes();
    } catch (e) {
      setError(e.message || 'Failed to disable code');
    }
  };

  const renderTable = (codes, showDisable) => (
    <table className="min-w-full divide-y divide-gray-200 table-fixed">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Redemptions</th>
          {showDisable && (
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          )}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {codes.length === 0 ? (
          <tr>
            <td colSpan={showDisable ? 6 : 5} className="px-4 py-6 text-center text-gray-500 text-sm">
              No codes in this section.
            </td>
          </tr>
        ) : (
          codes.map((c) => (
            <tr key={c.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.code}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{c.discount_percent}%</td>
              <td className="px-4 py-3 text-sm text-gray-500 cell-wrap">{formatDate(c.valid_from)}</td>
              <td className="px-4 py-3 text-sm text-gray-500 cell-wrap">{formatDate(c.valid_to)}</td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {c.redemption_count}
                {c.max_redemptions != null ? ` / ${c.max_redemptions}` : ' / unlimited'}
              </td>
              {showDisable && (
                <td className="px-4 py-3 text-sm">
                  <button
                    type="button"
                    onClick={() => handleDisable(c.id)}
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Disable
                  </button>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="header-with-button">
        <h2 className="text-xl font-semibold">Discount codes</h2>
        <button
          type="button"
          className="add-button"
          onClick={() => setShowModal(true)}
        >
          Generate discount code
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          <h3 className="text-lg font-medium mb-2 mt-4">Active codes</h3>
          <div className="overflow-x-auto mb-8">{renderTable(activeCodes, true)}</div>

          <h3 className="text-lg font-medium mb-2">Inactive codes</h3>
          <div className="overflow-x-auto">{renderTable(inactiveCodes, false)}</div>
        </>
      )}

      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => !submitting && setShowModal(false)}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Generate discount code</h3>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Code</label>
                <input
                  type="text"
                  required
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm uppercase"
                  placeholder="SUMMER20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Discount %</label>
                <input
                  type="number"
                  required
                  min={1}
                  max={99}
                  value={form.discount_percent}
                  onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  placeholder="15"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start date</label>
                  <input
                    type="date"
                    required
                    value={form.valid_from}
                    onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End date (required)</label>
                  <input
                    type="date"
                    required
                    value={form.valid_to}
                    min={form.valid_from || undefined}
                    onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max redemptions (optional)
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.max_redemptions}
                  onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  placeholder="100 — leave blank for unlimited"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium"
                >
                  {submitting ? 'Creating…' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
